/**
 * Mobile signature capture page — opened via QR code on the user's phone.
 * Route: /signature/:token
 *
 * Renders a full-screen canvas for touch/stylus ink capture, then POSTs the
 * signature dataUrl to the relay endpoint so the desktop session can receive it.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Text,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DismissCircleRegular,
  DeleteRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    boxSizing: 'border-box',
  },
  canvasWrapper: {
    width: '100%',
    maxWidth: '600px',
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#fff',
    touchAction: 'none',
  },
  canvas: {
    display: 'block',
    width: '100%',
    touchAction: 'none',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  statusIcon: {
    fontSize: '64px',
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

type Stage = 'draw' | 'uploading' | 'done' | 'error';

const SignatureApp: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const styles = useStyles();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const hasStrokes = useRef(false);

  const [stage, setStage] = useState<Stage>('draw');
  const [errorMsg, setErrorMsg] = useState('');

  // Size canvas to device width on mount and resize
  useEffect(() => {
    const size = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const w = canvas.offsetWidth;
      // Preserve existing drawing when resizing
      const img = canvas.toDataURL();
      canvas.width = w;
      canvas.height = Math.round(w * 0.35);
      if (hasStrokes.current) {
        const image = new Image();
        image.onload = () => canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
        image.src = img;
      }
    };
    size();
    window.addEventListener('resize', size);
    return () => window.removeEventListener('resize', size);
  }, []);

  const getPos = (e: React.TouchEvent | React.MouseEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return { x: ((e as React.MouseEvent).clientX - rect.left) * scaleX, y: ((e as React.MouseEvent).clientY - rect.top) * scaleY };
  };

  const startDraw = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    isDrawing.current = true;
    lastPos.current = getPos(e, canvas);
  }, []);

  const draw = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current!.x, lastPos.current!.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    hasStrokes.current = true;
  }, []);

  const endDraw = useCallback(() => {
    isDrawing.current = false;
    lastPos.current = null;
  }, []);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
  }, []);

  const handleSubmit = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !token) return;
    if (!hasStrokes.current) {
      setErrorMsg('Please draw your signature before submitting.');
      return;
    }
    setStage('uploading');
    const dataUrl = canvas.toDataURL('image/png');
    try {
      const res = await fetch(`/api/photo-relay/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageData: dataUrl, fileName: `signature_${Date.now()}.png` }),
      });
      if (res.ok) {
        setStage('done');
      } else if (res.status === 404 || res.status === 410) {
        setErrorMsg('This link has expired or already been used. Please scan a new QR code.');
        setStage('error');
      } else {
        setErrorMsg('Upload failed. Please try again.');
        setStage('error');
      }
    } catch {
      setErrorMsg('Upload failed. Please check your connection and try again.');
      setStage('error');
    }
  }, [token]);

  if (stage === 'uploading') {
    return (
      <div className={styles.page}>
        <Spinner size="large" />
        <Text>Sending signature...</Text>
      </div>
    );
  }

  if (stage === 'done') {
    return (
      <div className={styles.page}>
        <CheckmarkCircleRegular className={`${styles.statusIcon} ${styles.successIcon}`} />
        <Text size={600} weight="semibold">Signature captured!</Text>
        <Text className={styles.hint}>You can close this tab and return to your form.</Text>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className={styles.page}>
        <DismissCircleRegular className={`${styles.statusIcon} ${styles.errorIcon}`} />
        <Text size={500} weight="semibold">Something went wrong</Text>
        <Text className={styles.hint}>{errorMsg}</Text>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Text size={400} weight="semibold">Draw your signature</Text>
      <Text size={200} className={styles.hint}>Use your finger or stylus to sign below</Text>
      <div className={styles.canvasWrapper}>
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      {errorMsg && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{errorMsg}</Text>}
      <div className={styles.actions}>
        <Button appearance="primary" size="large" onClick={handleSubmit}>
          Submit Signature
        </Button>
        <Button appearance="secondary" size="large" icon={<DeleteRegular />} onClick={handleClear}>
          Clear
        </Button>
      </div>
    </div>
  );
};

export default SignatureApp;
